import { useEffect, useRef } from "react";

// Cloudflare Turnstile on the sign-up form.
//
// Added after 2026-08-07, when a bot pushed 56 sign-ups through the form in
// minutes; the bounced confirmation emails got our Mailtrap transactional
// stream suspended and passwordless sign-in stopped working for everyone.
//
// Renders nothing when REACT_APP_TURNSTILE_SITE_KEY is unset, so local and
// legacy builds keep working - the backend rule is inert without its secret.

const SCRIPT_SRC =
  "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
const SCRIPT_ID = "cf-turnstile-script";

declare global {
  interface Window {
    turnstile?: {
      render: (el: HTMLElement, opts: Record<string, unknown>) => string;
      remove: (id: string) => void;
      reset: (id: string) => void;
    };
  }
}

const loadScript = (): Promise<void> =>
  new Promise((resolve, reject) => {
    if (window.turnstile) return resolve();
    const existing = document.getElementById(SCRIPT_ID);
    if (existing) {
      existing.addEventListener("load", () => resolve());
      existing.addEventListener("error", () => reject());
      return;
    }
    const s = document.createElement("script");
    s.id = SCRIPT_ID;
    s.src = SCRIPT_SRC;
    s.async = true;
    s.defer = true;
    s.onload = () => resolve();
    s.onerror = () => reject();
    document.head.appendChild(s);
  });

interface Props {
  /** Fires with the token on success, and with "" when it expires or errors. */
  onVerify: (token: string) => void;
}

export const Turnstile = ({ onVerify }: Props): JSX.Element | null => {
  const siteKey = process.env.REACT_APP_TURNSTILE_SITE_KEY;
  const containerRef = useRef<HTMLDivElement>(null);

  // Keep the latest callback without re-rendering the widget: re-rendering
  // would issue a fresh token and invalidate the one already in the form.
  const onVerifyRef = useRef(onVerify);
  onVerifyRef.current = onVerify;

  useEffect(() => {
    if (!siteKey || !containerRef.current) return;

    let widgetId: string | undefined;
    let cancelled = false;

    loadScript()
      .then(() => {
        if (cancelled || !containerRef.current || !window.turnstile) return;
        widgetId = window.turnstile.render(containerRef.current, {
          sitekey: siteKey,
          theme: "light",
          callback: (token: string) => onVerifyRef.current(token),
          // Both clear the token so the submit button locks again and the
          // user is not left with a stale value the backend would reject.
          "expired-callback": () => onVerifyRef.current(""),
          "error-callback": () => onVerifyRef.current(""),
        });
      })
      .catch(() => {
        // Cloudflare unreachable. The backend rule fails open in that case,
        // so let the user through rather than blocking sign-up entirely.
        if (!cancelled) onVerifyRef.current("");
      });

    return () => {
      cancelled = true;
      if (widgetId && window.turnstile) {
        try {
          window.turnstile.remove(widgetId);
        } catch {
          /* widget already gone */
        }
      }
    };
  }, [siteKey]);

  if (!siteKey) return null;

  return <div ref={containerRef} className="turnstile-widget" />;
};
